import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Share2, Send, Loader2, Building2, Home,
  Linkedin, Facebook, Twitter, X, Copy,
} from "lucide-react";
import { TYPE_BY_VALUE } from "../lib/propertyTypes";
import SEO from "../components/SEO";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const publicClient = axios.create({ baseURL: `${API_BASE}/api` });

export default function Listing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prop, setProp] = useState(null);
  const [err, setErr] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Read ?utm_source=... from the URL once; forwarded into any lead submission.
  const utmSource = typeof window !== "undefined"
    ? (new URLSearchParams(window.location.search).get("utm_source") || "").slice(0, 32)
    : "";

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await publicClient.get(`/public/properties/${id}`);
        if (!cancel) setProp(data);
      } catch (e) {
        if (!cancel) setErr(true);
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen bg-marketing text-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <Home className="w-12 h-12 mx-auto text-white/30 mb-4" />
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Listing unavailable</h1>
          <p className="text-white/50 mt-2">This property is no longer available or the link is invalid.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-white/80 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back to RealtyVault
          </Link>
        </div>
      </div>
    );
  }

  if (!prop) {
    return (
      <div className="min-h-screen bg-marketing text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-white/50" />
      </div>
    );
  }

  const typeMeta = TYPE_BY_VALUE[prop.property_type];
  const locLine = [prop.city, prop.state].filter(Boolean).join(", ");
  const imgUrl = prop.has_image ? `${API_BASE}/api/public/properties/${id}/image?size=1200x600` : null;
  const mapUrl = prop.lat != null && prop.lng != null
    ? `https://www.google.com/maps?q=${prop.lat},${prop.lng}&z=15`
    : null;

  const typeLabel = TYPE_BY_VALUE[prop.property_type]?.label || "Property";
  const location = [prop.city, prop.state].filter(Boolean).join(", ") || "available";
  const listingDesc = `${prop.name} — ${typeLabel} in ${location}. Contact the owner directly on RealtyVault.`;
  const listingImg = `${API_BASE}/api/public/properties/${id}/image?size=1200x600`;

  return (
    <div className="min-h-screen bg-marketing text-white">
      <SEO
        title={`${prop.name} · ${typeLabel}`}
        description={listingDesc}
        path={`/listings/${id}`}
        image={listingImg}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "RealEstateListing",
          name: prop.name,
          description: listingDesc,
          image: listingImg,
          url: `https://realtyvaultmgr.com/listings/${id}`,
          ...(prop.city || prop.state
            ? {
                address: {
                  "@type": "PostalAddress",
                  addressLocality: prop.city || undefined,
                  addressRegion: prop.state || undefined,
                  addressCountry: "US",
                },
              }
            : {}),
          ...(prop.lat != null && prop.lng != null
            ? {
                geo: {
                  "@type": "GeoCoordinates",
                  latitude: prop.lat,
                  longitude: prop.lng,
                },
              }
            : {}),
        }}
      />
      <header className="px-6 md:px-12 lg:px-24 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="listing-back">
          <div className="w-8 h-8 rounded-md bg-white text-slate-900 flex items-center justify-center font-bold text-sm">R</div>
          <span className="font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>RealtyVault <span className="text-white/50">Manager</span></span>
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShareOpen(true)}
            data-testid="listing-share-button"
            className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-full border border-white/20 hover:bg-white/10 flex items-center gap-1.5 transition-colors"
          >
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
          <Link
            to="/signup"
            className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-full bg-white text-slate-900 hover:bg-slate-100 transition-colors hidden sm:inline-block"
          >
            List your property →
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="px-6 md:px-12 lg:px-24 pb-8 max-w-6xl mx-auto">
        <div className="rounded-2xl overflow-hidden bg-white/5 border border-white/10" data-testid="listing-hero">
          {imgUrl ? (
            <img
              src={imgUrl}
              alt={prop.name}
              className="w-full h-[380px] md:h-[480px] object-cover"
              data-testid="listing-image"
            />
          ) : (
            <div className="w-full h-[380px] flex items-center justify-center bg-white/5 text-white/30">
              <MapPin className="w-10 h-10" />
            </div>
          )}
        </div>

        <div className="mt-8 grid md:grid-cols-[1fr_auto] gap-6 items-start">
          <div>
            <div className="overline text-white/40 mb-2">Available listing</div>
            <div className="flex items-start gap-3">
              <span className="text-4xl">{typeMeta?.emoji || "🏢"}</span>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold tracking-tighter" style={{ fontFamily: "Outfit" }} data-testid="listing-name">
                  {prop.name}
                </h1>
                <div className="mt-2 text-white/60 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-mono uppercase tracking-widest">
                  <span>{typeMeta?.label || prop.property_type}</span>
                  {prop.units != null && <><span className="text-white/30">·</span><span>{prop.units} unit{prop.units === 1 ? "" : "s"}</span></>}
                  {locLine && <><span className="text-white/30">·</span><span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{locLine}</span></>}
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => setLeadOpen(true)}
            data-testid="listing-contact-button"
            className="bg-white text-slate-900 hover:bg-slate-100 px-6 py-4 rounded-full font-bold text-sm flex items-center gap-2 transition-transform hover:-translate-y-0.5 shadow-lg"
          >
            <Send className="w-4 h-4" /> Contact owner
          </button>
        </div>
      </section>

      {/* DETAIL GRID */}
      <section className="px-6 md:px-12 lg:px-24 pb-16 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-3 gap-4">
          <DetailCard label="Property type" value={typeMeta?.label || prop.property_type} sub={typeMeta?.emoji || ""} />
          <DetailCard label="Units" value={prop.units != null ? String(prop.units) : "—"} sub={prop.units === 1 ? "single unit" : "income-producing"} />
          <DetailCard label="Location" value={locLine || "—"} sub={mapUrl ? "Tap to open in Google Maps" : ""} href={mapUrl} />
        </div>

        <div className="mt-10 rounded-2xl bg-white/5 border border-white/10 p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-xl">
            <div className="overline text-white/40">Owner</div>
            <h3 className="mt-2 text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
              {prop.owner_display_name} is ready to connect.
            </h3>
            <p className="mt-2 text-white/50 text-sm">
              RealtyVault keeps your contact info private until the owner replies. No spam, no intermediaries — just a direct line to the person who manages this property.
            </p>
          </div>
          <button
            onClick={() => setLeadOpen(true)}
            data-testid="listing-contact-button-cta"
            className="bg-white text-slate-900 hover:bg-slate-100 px-6 py-3 rounded-full font-bold text-sm flex items-center gap-2 transition-colors whitespace-nowrap"
          >
            <Send className="w-4 h-4" /> Send a message
          </button>
        </div>
      </section>

      {/* MARKETING FOOTER */}
      <section className="px-6 md:px-12 lg:px-24 pb-16 max-w-6xl mx-auto">
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-sm">
          <div className="text-white/50">
            Powered by <Link to="/" className="text-white font-bold hover:underline">RealtyVault Manager</Link> — the operator console for commercial real estate.
          </div>
          <button
            onClick={() => navigate("/#near-me")}
            data-testid="listing-search-nearby"
            className="text-white/70 hover:text-white font-mono uppercase tracking-widest text-xs flex items-center gap-1.5"
          >
            <MapPin className="w-3.5 h-3.5" /> See other properties nearby →
          </button>
        </div>
      </section>

      {leadOpen && (
        <LeadDialog property={prop} utmSource={utmSource} onClose={() => setLeadOpen(false)} />
      )}
      {shareOpen && (
        <ShareDialog
          canonicalUrl={typeof window !== "undefined" ? window.location.href : ""}
          shareUrl={`${API_BASE}/api/og/listings/${id}`}
          title={prop.name}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}

function DetailCard({ label, value, sub, href }) {
  const body = (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6 h-full transition-colors hover:bg-white/10">
      <div className="overline text-white/40">{label}</div>
      <div className="mt-2 text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>{value}</div>
      {sub && <div className="mt-1 text-[11px] text-white/50 font-mono uppercase tracking-widest">{sub}</div>}
    </div>
  );
  return href ? <a href={href} target="_blank" rel="noreferrer" className="block">{body}</a> : body;
}

function LeadDialog({ property, utmSource, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await publicClient.post("/public/leads", {
        property_id: property.id,
        name: form.name, email: form.email, phone: form.phone, message: form.message,
        source: "listing_page",
        utm_source: utmSource || null,
      });
      toast.success("Message sent — the owner will be in touch.");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="listing-lead-dialog"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white text-slate-900 p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline text-slate-500">Contact owner</div>
            <h4 className="text-xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>{property.name}</h4>
            <div className="text-xs font-mono text-slate-500 mt-0.5 flex items-center gap-1">
              <Building2 className="w-3 h-3" /> {[property.city, property.state].filter(Boolean).join(", ") || "—"}
            </div>
          </div>
          <button onClick={onClose} data-testid="listing-lead-close" className="text-slate-400 hover:text-slate-900 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-3 text-sm">
          <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-slate-400" data-testid="listing-lead-name" />
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-slate-400" data-testid="listing-lead-email" />
          <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-slate-400" data-testid="listing-lead-phone" />
          <textarea rows={3} placeholder="What would you like to know?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-slate-400" data-testid="listing-lead-message" />
          <button type="submit" disabled={busy} data-testid="listing-lead-submit"
            className="w-full py-2.5 rounded-md bg-slate-900 text-white font-bold hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send inquiry
          </button>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 text-center">
            We never share your info. Reply comes from the property owner.
          </div>
        </form>
      </div>
    </div>
  );
}

function ShareDialog({ canonicalUrl, shareUrl, title, onClose }) {
  const copy = async () => {
    try {
      // Humans copy the canonical /listings/:id URL (clean + clicks straight into the React app)
      await navigator.clipboard.writeText(canonicalUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };
  // Social share URL points to the backend og endpoint so LinkedIn/Facebook/X
  // crawlers unfurl a rich preview. Real humans who click get instantly
  // redirected to the canonical React listing page. Each platform gets its
  // own utm_source so lead attribution shows which social drove the click.
  const ogBase = shareUrl;
  const tagged = (src) => `${ogBase}${ogBase.includes("?") ? "&" : "?"}utm_source=${src}`;
  const shareLinks = [
    { label: "LinkedIn",    Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(tagged("linkedin"))}` },
    { label: "Facebook",    Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(tagged("facebook"))}` },
    { label: "X (Twitter)", Icon: Twitter,  href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(tagged("twitter"))}&text=${encodeURIComponent(title)}` },
  ];
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm" onClick={onClose} data-testid="listing-share-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl bg-white text-slate-900 p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline text-slate-500">Share this listing</div>
            <div className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>{title}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1" data-testid="listing-share-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="mt-5 space-y-2">
          {shareLinks.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noreferrer"
               className="flex items-center gap-3 px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 text-sm font-medium"
               data-testid={`listing-share-${s.label.toLowerCase().replace(/\s.*$/, "")}`}>
              <s.Icon className="w-4 h-4" /> Share on {s.label}
            </a>
          ))}
          <button onClick={copy} data-testid="listing-share-copy"
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 text-sm font-medium">
            <Copy className="w-4 h-4" /> Copy link
          </button>
        </div>
        <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-slate-400 break-all text-center">{canonicalUrl}</div>
      </div>
    </div>
  );
}
